import { Flex, Typography } from 'antd';
import { BRAND_TAN } from '../app/theme';

/**
 * The church's name for this app, and the marks that carry it.
 *
 * It lived in four files before — the document title, the app shell header, the
 * signed-out card and the pastor's landing screen — which is three chances to
 * rename three of them. The strings are here; `index.html` carries the fourth
 * copy because a `<title>` is read before any of this runs.
 *
 * **The artwork is SION's own**, supplied by the office as a 920×380 PNG — a
 * horizontal lockup: a hexagonal `S` monogram holding a cross, then the SION
 * wordmark, in navy `#01247e` with cyan accents. Cropped to the ink and
 * downscaled into `public/`:
 *
 * - `sion-mark.png` — the monogram alone, **padded to a square**. The monogram
 *   is 252×322, so a square box is what lets it be drawn at any size without a
 *   `width`/`height` pair having to approximate that ratio and squash it.
 * - `sion-logo.png` — the whole lockup, 600×217. Where it is used, the wordmark
 *   in the artwork *is* the app's name, so nothing repeats it in type.
 * - `favicon.png` — the monogram again, squared, at 130px.
 *
 * Same-origin files, which is what `img-src 'self'` in the document CSP allows
 * — nothing here may be loaded from a CDN.
 *
 * **The palette is deliberately not this logo's.** The theme follows
 * `gkiserpong.org` — tan, sienna, cream — and the navy mark sits on it as the
 * app's own badge rather than as the source of its colours (decided
 * 2026-09-11). Moving the theme onto the logo's navy is a separate job, with
 * every ratio in `theme.ts` to re-check.
 */
export const APP_NAME = 'SION';
export const CHURCH_NAME = 'GKI Serpong';

/**
 * Archivo, the display face `gkiserpong.org` sets its one big heading in. Named
 * here rather than in `theme.ts` because these two components are the only
 * things in the app that use it — AntD's `fontFamily` token is Roboto, and
 * headings are Raleway (`src/index.css`).
 */
const DISPLAY_FACE = "Archivo, ui-sans-serif, system-ui, 'Segoe UI', Arial, sans-serif";

/** The site's sienna, darkened so it can carry 14–16px text. 5.64:1 on white. */
const TITLE_COLOR = '#a0521e';
/** Body ink, for the church's name under the app's. */
const SUBTITLE_COLOR = 'rgba(0, 0, 0, 0.65)';
/**
 * The ink on the welcome panel — **dark, because the panel is light.**
 *
 * The panel went through a dark sienna (white text at 0.88 alpha) and a lighter
 * clay (solid white) before landing on the church's tan, `#d19a7a`. White does
 * not survive that last step by any amount of tuning: it is 2.44:1 there, and
 * `gkiserpong.org` draws black on this same band rather than pretending
 * otherwise. This is a warm near-black instead of a true one, so it belongs to
 * the palette — 6.00:1 on the tan, 5.00:1 on the gradient's far stop.
 *
 * Solid, with no alpha. The hierarchy between the heading and the copy under it
 * is carried by size and weight, which is a habit worth keeping: every time
 * this panel's background moved, the transparent text was the thing that broke.
 */
const ON_BRAND = '#3d2213';

/**
 * The horizontal lockup in the app shell's 56px header: the mark, the app's
 * name, the church's.
 *
 * **The monogram alone here, not the lockup.** The header is 56px, so a lockup
 * would render its wordmark at about 10px — legible on a design mock and not on
 * a laptop. The name stays in type beside it, where it can be read at any size.
 *
 * Presentational — the `alt` is empty on purpose. The mark repeats the words
 * printed next to it, and a screen reader that announces "SION logo, SION, GKI
 * Serpong" has read the same thing twice. Whoever wraps this in a link supplies
 * the accessible name from that text.
 */
export function BrandLockup() {
  return (
    <Flex align="center" gap={10}>
      <img src="/sion-mark.png" alt="" width={30} height={30} style={{ display: 'block' }} />
      <Typography.Text
        style={{ fontFamily: DISPLAY_FACE, fontWeight: 700, fontSize: 17, color: TITLE_COLOR, letterSpacing: '0.04em' }}
      >
        {APP_NAME}
      </Typography.Text>
      <Typography.Text type="secondary" style={{ letterSpacing: '0.02em' }}>
        {CHURCH_NAME}
      </Typography.Text>
    </Flex>
  );
}

/**
 * The stacked lockup at the top of the signed-out card, arranged the way
 * `gkiserpong.org` arranges its own masthead: the mark, then the name, then a
 * letter-spaced line under it, then the motto.
 *
 * **The name is the artwork here, not type.** It was set in 30px Archivo
 * sienna until the office supplied a lockup that draws its own wordmark; both
 * at once reads "SION" twice. So this `alt` is *not* empty, unlike the header's
 * — that one sits beside the name in text and would repeat it, while here the
 * image is the only thing that says what the app is called.
 *
 * 177×64 is the asset's own 600×217 at a scale that lands on whole pixels: the
 * ratio is 2.765, and this pair is 0.02% off it. Both dimensions are set rather
 * than one, so the card does not reflow when the image arrives.
 *
 * The motto's separators are **drawn, not typed**. Written as "•" they would be
 * text, and the brand tan on white is 2.44:1 — a contrast failure axe is right
 * to report (`e2e/accessibility.spec.ts`). An empty span with a background
 * colour is a decoration, which is the thing it actually is.
 */
export function BrandBadge() {
  return (
    <Flex vertical align="center" gap={8}>
      <img src="/sion-logo.png" alt={APP_NAME} width={177} height={64} style={{ display: 'block', marginBottom: 4 }} />
      <Typography.Text style={{ fontSize: 12, fontWeight: 500, letterSpacing: '0.22em', color: SUBTITLE_COLOR }}>
        {CHURCH_NAME.toUpperCase()}
      </Typography.Text>

      <Flex align="center" gap={8} style={{ width: '100%', marginTop: 4 }}>
        <Rule />
        {['Bersatu', 'Bertumbuh', 'Berdampak'].map((word) => (
          <Flex key={word} align="center" gap={5}>
            <Dot />
            <Typography.Text style={{ fontSize: 12, fontWeight: 500, color: TITLE_COLOR }}>{word}</Typography.Text>
          </Flex>
        ))}
        <Rule />
      </Flex>
    </Flex>
  );
}

/**
 * The panel beside the sign-in form: the church's welcome, on the church's
 * colour (F1.1).
 *
 * Modelled on `gki.one/login`, which greets people on a full-height coloured
 * half and keeps the form on the other — the shape this app was asked for. The
 * colour is ours, not theirs: a sienna gradient out of `theme.ts` rather than
 * their coral.
 *
 * **The panel is the church's tan and the ink on it is dark** (`ON_BRAND`),
 * which is how `gkiserpong.org` treats the same colour. Dark-on-light also
 * inverts which end of the gradient is the risk: white text fails first on the
 * *lightest* ground, dark text on the *darkest*, so the far stop is the one to
 * watch and `e2e/accessibility.spec.ts` scores every stop rather than assuming
 * either.
 *
 * **The lockup is the navy one, and it is the only navy on this panel.** The
 * theme stayed warm when the artwork changed (see the note at the top of this
 * file), so the mark reads as a badge on the tan rather than as a colour the
 * panel is trying to match. A white variant would be the 2.44:1 problem drawn
 * as a picture, which is the same reason the old charcoal mark was used here.
 *
 * The `alt` is empty because the heading under it already says the app's name
 * in a sentence — unlike `BrandBadge`, where the artwork carries the name alone.
 *
 * The caller sets the background; this draws only the content, so the two
 * cannot disagree about which gradient the ratios were computed against.
 */
export function BrandWelcome() {
  return (
    <Flex vertical align="flex-start" gap={20} style={{ maxWidth: 460 }}>
      <img src="/sion-logo.png" alt="" width={188} height={68} style={{ display: 'block' }} />

      <Typography.Title
        level={1}
        style={{
          margin: 0,
          fontFamily: DISPLAY_FACE,
          fontSize: 42,
          fontWeight: 700,
          lineHeight: 1.14,
          color: ON_BRAND,
        }}
      >
        Selamat datang di {APP_NAME}
      </Typography.Title>

      <Typography.Paragraph style={{ margin: 0, fontSize: 16, lineHeight: 1.6, color: ON_BRAND }}>
        Aplikasi kantor {CHURCH_NAME} untuk data jemaat, susunan keluarga dan status keanggotaan — satu catatan yang
        dipakai bersama, dengan setiap perubahan tercatat.
      </Typography.Paragraph>

      <Flex align="center" gap={10} style={{ marginTop: 4 }}>
        {['Bersatu', 'Bertumbuh', 'Berdampak'].map((word) => (
          <Flex key={word} align="center" gap={6}>
            <Dot tone="panel" />
            <Typography.Text style={{ fontSize: 13, fontWeight: 500, letterSpacing: '0.04em', color: ON_BRAND }}>
              {word}
            </Typography.Text>
          </Flex>
        ))}
      </Flex>

      {/* Not decoration: a MEMBER account is refused by this form (`api.ts`,
          NotBackOfficeError), and saying so before the attempt is kinder than
          saying it after. */}
      <Typography.Text style={{ marginTop: 12, fontSize: 13, color: ON_BRAND }}>
        Khusus untuk staf dan pengurus gereja.
      </Typography.Text>
    </Flex>
  );
}

function Rule() {
  return <span style={{ flex: 1, height: 1, background: BRAND_TAN, opacity: 0.6 }} />;
}

function Dot({ tone = 'brand' }: { tone?: 'brand' | 'panel' }) {
  return (
    <span
      style={{
        width: 5,
        height: 5,
        borderRadius: '50%',
        background: tone === 'panel' ? ON_BRAND : BRAND_TAN,
        flex: 'none',
      }}
    />
  );
}
